import Add from '@mui/icons-material/Add';
import Settings from '@mui/icons-material/Settings';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { TREAD_UUID } from '~hooks/useCompany/useCompany';
import { ReportCategory } from '~hooks/useOmniReports/models';
import InfiniteList from '~pages/Dispatch/components/drivers/InfiniteList';

import { UseReportCategoryQueryResult } from './useReportCategoryQuery';

interface ReportCategoriesListProps {
  companyId: string;
  reportCategoryQuery: UseReportCategoryQueryResult;
  selectedReportCategory: string | boolean;
  setSelectedReportCategory: (value: string | boolean) => void;
  setIsDialogOpen: (value: boolean) => void;
  setEditReportCategory: (category: ReportCategory | null) => void;
}

const REPORT_CATEGORY_HEIGHT_PX = 48;
const REPORT_SETTINGS_SIDEBAR_WIDTH = 292;

export const ReportCategoriesList = ({
  companyId,
  reportCategoryQuery,
  selectedReportCategory,
  setSelectedReportCategory,
  setIsDialogOpen,
  setEditReportCategory,
}: ReportCategoriesListProps) => {
  const handleReportCategoryChange = (_event: React.SyntheticEvent, newValue: string) => {
    setSelectedReportCategory(newValue);
  };

  const handleEditReportCategory = (category: ReportCategory) => {
    setEditReportCategory(category);
    setIsDialogOpen(true);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid lightgray',
        mt: 2,
      }}
    >
      <Button
        color="primary"
        variant="contained"
        sx={{ minWidth: '260px', m: 2 }}
        startIcon={<Add />}
        onClick={() => setIsDialogOpen(true)}
      >
        {t('administration.report_settings.create_page')}
      </Button>
      <Divider />
      <Box sx={{ height: '600px' }}>
        {reportCategoryQuery.loading ? (
          <Box
            sx={{
              display: 'flex',
              height: '100%',
            }}
          >
            <LoadingSpinner isVisible />
          </Box>
        ) : (
          <List
            disablePadding
            sx={{
              borderLeft: 1,
              borderColor: 'divider',
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <InfiniteList
              hasNextPage={reportCategoryQuery.hasMore}
              itemCount={reportCategoryQuery.data.length}
              itemSize={REPORT_CATEGORY_HEIGHT_PX}
              items={reportCategoryQuery.data}
              loadMoreItems={reportCategoryQuery.fetchMore}
              width={REPORT_SETTINGS_SIDEBAR_WIDTH}
              renderItem={(index, style) => {
                const category = reportCategoryQuery.data[index];
                if (!category) {
                  throw new Error('Invalid category');
                }

                return (
                  <ListItemButton
                    key={category.id}
                    selected={selectedReportCategory === category.id}
                    onClick={() =>
                      handleReportCategoryChange({} as React.SyntheticEvent, category.id)
                    }
                    sx={{
                      ...style,
                      display: 'flex',
                      alignItems: 'center',
                      '&.Mui-selected': {
                        '&:hover': {
                          backgroundColor: 'action.selected',
                        },
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: 4,
                          backgroundColor: 'primary.main',
                        },
                      },
                    }}
                  >
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2">{category.name}</Typography>
                    </Box>
                    {category.system && (
                      <Badge
                        sx={{
                          mr: 1,
                          '& .MuiBadge-badge': {
                            position: 'relative',
                            transform: 'none',
                            fontSize: '12px',
                            height: '20px',
                            padding: '0 6px',
                            backgroundColor: (theme) => theme.brandV2.colors.treadGray8,
                            color: (theme) => theme.brandV2.colors.treadGray2,
                            border: (theme) =>
                              `1px solid ${theme.brandV2.colors.treadGray7}`,
                          },
                        }}
                        badgeContent={t('common.system')}
                      />
                    )}
                    {(!category.system ||
                      (category.system && companyId === TREAD_UUID)) && (
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditReportCategory(category);
                        }}
                        size="small"
                      >
                        <Settings />
                      </IconButton>
                    )}
                  </ListItemButton>
                );
              }}
            />
          </List>
        )}
      </Box>
    </Box>
  );
};
